'use client'
import { StoreApi, UseBoundStore, create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Context, createContext, memo, useContext, useMemo, useRef } from 'react'
import { TGetWeb3WalletChainAllResponse, TGetWeb3WalletTokenAllResponse } from 'core/service/yapi/hk-web/Web3Wallet/api'

export type TAccountAddress = {
  chainIndex: string
  address: string
  remark: string
}[]

export type TChainList = {
  address: string
  balance: string
  chainIndex: string
  isMultiChainNetwork: boolean
  isRiskToken: boolean
  logo: string
  rawBalance: string
  symbol: string
  tokenAddress: string
  tokenPrice: string
  tokenType: string
  chainList: TChainList[]
}

type TWalletStore = {
  walletInfo: {
    accountId: string
    accountName: string
    colorId: number
    device: string
    ip: string
    walletName: string
  }
  chainAll: TGetWeb3WalletChainAllResponse['data'] | []
  coinAll: TGetWeb3WalletTokenAllResponse['data'] | []
  accountAddress: TAccountAddress | []
  assetChainIndex: string | ''
  setChainAll: (chainAll: TGetWeb3WalletChainAllResponse['data'] | []) => void
  setCoinAll: (coinAll: TGetWeb3WalletTokenAllResponse['data'] | []) => void
  setAccountAddress: (accountAddress: TAccountAddress | []) => void
  setAssetChainIndex: (chainIndex: string | null) => void
  setWalletInfo: (walletInfo: TWalletStore['walletInfo'] | null) => void
  chainList: TChainList
  setChainList: (chainList: TChainList) => void
  showNumbers: boolean | false
  setShowNumbers: (showNumbers: boolean | false) => void
  totalBalance: string
  setTotalBalance: (totalBalance: string) => void
}

type TWalletContext = {
  store: UseBoundStore<StoreApi<TWalletStore>> | null
}

type TProps = {
  children: React.ReactNode
}
const WalletContext = createContext<TWalletContext>({
  store: null,
})

const createWalletStoreProvider = (WalletContext: Context<TWalletContext>) => {
  const ContractStoreProvider = memo((props: TProps) => {
    const storeRef = useRef({
      store: create<TWalletStore>()(
        persist(
          (set) => ({
            walletInfo: {
              accountId: '',
              accountName: '',
              colorId: 0,
              device: '',
              ip: '',
              walletName: '',
            },
            chainAll: [],
            coinAll: [],
            accountAddress: [],
            assetChainIndex: '',
            setChainAll: (chainAll: TGetWeb3WalletChainAllResponse['data'] | []) => {
              set(() => ({
                chainAll: chainAll,
              }))
            },
            setCoinAll: (coinAll: TGetWeb3WalletTokenAllResponse['data'] | []) => {
              set(() => ({
                coinAll: coinAll,
              }))
            },
            setAccountAddress: (accountAddress: TAccountAddress | []) => {
              set(() => ({
                accountAddress: accountAddress,
              }))
            },
            setAssetChainIndex: (assetChainIndex: string | null) => {
              set(() => ({
                assetChainIndex: assetChainIndex,
              }))
            },
            setWalletInfo: (data) => {
              set(() => ({
                walletInfo: data,
              }))
            },
            chainList: {
              address: '',
              balance: '',
              chainIndex: '',
              isMultiChainNetwork: false,
              isRiskToken: false,
              logo: '',
              rawBalance: '',
              symbol: '',
              tokenAddress: '',
              tokenPrice: '',
              tokenType: '',
              chainList: [],
            },
            setChainList: (chainList: TChainList) => {
              set(() => ({
                chainList: chainList,
              }))
            },
            showNumbers: false,
            setShowNumbers: (showNumbers: boolean | false) => {
              set(() => ({
                showNumbers: showNumbers,
              }))
            },
            totalBalance: '0',
            setTotalBalance: (totalBalance: string) => {
              set(() => ({
                totalBalance: totalBalance,
              }))
            },
          }),
          {
            name: 'walletStore',
          }
        )
      ),
    })

    return useMemo(() => {
      return <WalletContext.Provider value={storeRef.current!}>{props.children}</WalletContext.Provider>
    }, [])
  })

  ContractStoreProvider.displayName = 'WalletStoreProvider'
  return ContractStoreProvider
}

export const WalletStoreProvider = createWalletStoreProvider(WalletContext)

export const useSetWalletInfo = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useSetWalletInfo must be used within a WalletStoreProvider')
  return store((s) => s.setWalletInfo)
}

export const useWalletInfo: <T extends keyof TWalletStore['walletInfo']>(
  key: T
) => TWalletStore['walletInfo'][T] | undefined = (key) => {
  const { store } = useContext(WalletContext)!
  if (!store) throw new Error('useWalletInfo must be used within a WalletStoreProvider')
  return store(({ walletInfo }) => walletInfo?.[key])
}

export const useChainAll = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useChainAll must be used within a WalletStoreProvider')
  return store((s) => s.chainAll)
}

export const useSetChainAll = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useSetChainAll must be used within a WalletStoreProvider')
  return store((s) => s.setChainAll)
}

export const useCoinAll = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useCoinAll must be used within a WalletStoreProvider')
  return store((s) => s.coinAll)
}
export const useSetCoinAll = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useSetCoinAll must be used within a WalletStoreProvider')
  return store((s) => s.setCoinAll)
}
export const useAccountAddress = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useAccountAddress must be used within a WalletStoreProvider')
  return store((s) => s.accountAddress)
}

export const useSetAccountAddress = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useSetAccountAddress must be used within a WalletStoreProvider')
  return store((s) => s.setAccountAddress)
}

export const useAssetChainIndex = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useAssetChainIndex must be used within a WalletStoreProvider')
  return store((s) => s.assetChainIndex)
}

export const useSetAssetChainIndex = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useSetAssetChainIndex must be used within a WalletStoreProvider')
  return store((s) => s.setAssetChainIndex)
}

export const useChainList = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useChainList must be used within a WalletStoreProvider')
  return store((s) => s.chainList)
}

export const useSetChainList = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useSetChainList must be used within a WalletStoreProvider')
  return store((s) => s.setChainList)
}

export const useShowNumbers = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useShowNumbers must be used within a WalletStoreProvider')
  return store((s) => s.showNumbers)
}

export const useSetShowNumbers = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useSetShowNumbers must be used within a WalletStoreProvider')
  return store((s) => s.setShowNumbers)
}

export const useTotalBalance = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useTotalBalance must be used within a WalletStoreProvider')
  return store((s) => s.totalBalance)
}

export const useSetTotalBalance = () => {
  const { store } = useContext(WalletContext)
  if (!store) throw new Error('useSetTotalBalance must be used within a WalletStoreProvider')
  return store((s) => s.setTotalBalance)
}
